import React from 'react';
import { useParams } from 'react-router-dom';
import { Button, Card, Spinner, Alert } from 'react-bootstrap';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import DashboardLayout from '../components/dashboard/DashboardLayout';
import InvoiceDetailContent from '../components/InvoiceDetailContent';
import { qboInvoiceUrl } from '../components/InvoiceDetailContent';
import { getInvoicesId, postInvoicesIdSync } from '../api/generated/arrayscapeGamingDashboardAPI';

const InvoiceDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const queryClient = useQueryClient();

  const { data: invoice, isLoading, error } = useQuery({
    queryKey: ['invoice', id],
    queryFn: () => getInvoicesId(id || ''),
    enabled: !!id,
  });

  const [resyncing, setResyncing] = React.useState(false);
  const handleResync = React.useCallback(async () => {
    if (!id) return;
    setResyncing(true);
    try {
      await postInvoicesIdSync(id);
      await queryClient.invalidateQueries({ queryKey: ['invoice', id] });
    } finally {
      setResyncing(false);
    }
  }, [id, queryClient]);

  if (isLoading) {
    return (
      <DashboardLayout title="Invoice">
        <div className="text-center py-5"><Spinner /></div>
      </DashboardLayout>
    );
  }

  if (error || !invoice) {
    return (
      <DashboardLayout title="Invoice">
        <Alert variant="danger" className="m-3">Invoice not found.</Alert>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout title={`Invoice #${invoice.invoice_number}`}>
      <Card className="d-flex flex-column flex-grow-1" style={{ minHeight: 0, overflow: 'hidden' }}>
        <Card.Header className="bg-primary text-white flex-shrink-0">
          <div className="d-flex justify-content-between align-items-center m-1 flex-wrap gap-2">
            <div className="d-flex align-items-center gap-3">
              <span className="fw-semibold fs-5">Invoice #{invoice.invoice_number}</span>
              {invoice.customer?.name && (
                <span className="text-white-50">{invoice.customer.name}</span>
              )}
            </div>
            <div className="d-flex align-items-center gap-2">
              <Button
                size="sm"
                variant="outline-light"
                onClick={handleResync}
                disabled={resyncing}
                title="Refetch this invoice (and its linked payments) from QuickBooks and re-run the auto-linker"
                className="d-inline-flex align-items-center gap-1"
              >
                {resyncing
                  ? <><Spinner as="span" animation="border" size="sm" role="status" aria-hidden="true" /><span>Resyncing…</span></>
                  : <><i className="bi bi-arrow-clockwise" /><span>Resync</span></>}
              </Button>
              {qboInvoiceUrl(invoice) && (
                <a href={qboInvoiceUrl(invoice)!} target="_blank" rel="noopener noreferrer" className="btn btn-sm btn-outline-light">
                  Open in QuickBooks
                </a>
              )}
            </div>
          </div>
        </Card.Header>
        <Card.Body className="p-3 d-flex flex-column flex-grow-1" style={{ minHeight: 0 }}>
          <InvoiceDetailContent invoice={invoice} onInvoiceUpdated={(updated) => queryClient.setQueryData(['invoice', id], updated)} />
        </Card.Body>
      </Card>
    </DashboardLayout>
  );
};

export default InvoiceDetailPage;
